/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { StyledProvisioningPageHeading } from '../StyledProvisioningPageHeading';
import { StyledProvisioningPageSubheading } from '../StyledProvisioningPageSubheading';
import { Stack } from '@mui/material';
import { BeamInput } from '../../common/formik/BeamInput';
import ErrorMessage from '../../common/layout/ErrorMessage';
import { BeamCancelButton } from '../../common/formik/BeamCancelButton';
import { BeamSubmitButton } from '../../common/formik/BeamSubmitButton';
import { useParams } from 'react-router-dom';
import { ajaxRaw } from '../../../utils/StoreUtils';
import * as yup from 'yup';
import { Role } from '../../../shared/entities/role';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectCurrentRole, selectCurrentRoleRequestError, selectIsRequestingCurrentRole } from '../../../store';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import { clearCurrentRole, requestCurrentRole } from '../../../store/roles/actions';
import { useAjaxFormSubmit } from '../../common/formik/useAjaxFormSubmit';

export const RolesForm = () => {
  const dispatch = useDispatch();
  let { uuid } = useParams();

  const roleToEdit = useSelector(selectCurrentRole, shallowEqual);
  const isLoadingCurrentRole = useSelector(selectIsRequestingCurrentRole, shallowEqual);
  const currentRoleRequestError = useSelector(selectCurrentRoleRequestError, shallowEqual);

  const [submissionError, setError] = useState<string | undefined>(undefined);
  const [initialFormState, setInitialFormState] = useState<Partial<Role>>({
    id: '',
    name: '',
    description: '',
  });
  const isEditMode = uuid !== undefined;
  const action = isEditMode ? 'Edit' : 'Add';
  const ajaxFormSubmit = useAjaxFormSubmit();

  useEffect(() => {
    if (isEditMode) {
      if (!roleToEdit) {
        // Request existing data if we're editing a Role
        dispatch(requestCurrentRole(uuid));
      } else {
        // Update form state when roleToEdit changes
        setInitialFormState({
          ...roleToEdit,
        });
      }
    }
  }, [isEditMode, uuid, roleToEdit, dispatch]);

  // Update error message if an error occurs fetching the current role
  useEffect(() => {
    if (currentRoleRequestError) {
      setError(currentRoleRequestError);
    }
  }, [currentRoleRequestError]);

  // Clear current role on unmount
  useEffect(() => {
    return () => {
      dispatch(clearCurrentRole());
    };
  }, [dispatch]);

  return (
    <>
      <StyledProvisioningPageHeading>
        {action} {!isEditMode && 'new '}role
      </StyledProvisioningPageHeading>
      <StyledProvisioningPageSubheading>
        To {action.toLowerCase()} a {!isEditMode && 'new '}role, please complete the form below.
      </StyledProvisioningPageSubheading>

      {isLoadingCurrentRole ? <ModalLoadingSpinner /> : null}

      <Formik
        enableReinitialize={true}
        initialValues={initialFormState}
        onSubmit={(values, { setSubmitting }: FormikHelpers<Partial<Role>>) => {
          setError(undefined);
          setSubmitting(true);

          let url = `/provisioning/roles`;
          let operation = ajaxRaw.postJson;
          if (isEditMode) {
            url = `/provisioning/roles/${values.id}`;
            operation = ajaxRaw.put;
          }

          ajaxFormSubmit(operation, url, values, setError, setSubmitting);
        }}
        validateOnChange={true}
        validationSchema={yup.object().shape({
          name: yup
            .string()
            .required('Please enter a unique name for the role.')
            .max(50, 'Role name must be less than 51 characters.')
            .matches(
              /^[A-Z0-9]+(_[A-Z0-9]+)*$/,
              'Role name can only contain capital letters and numbers, separated by underscores.',
            ),
          description: yup
            .string()
            .required('Please enter a description for the role.')
            .max(120, 'Role description must be less than 121 characters.'),
        })}
      >
        <Form style={{ width: '600px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {submissionError ? <ErrorMessage>{submissionError}</ErrorMessage> : null}

          <BeamInput name={'name'} label={'Role Name'} required={!isEditMode} disableInput={isEditMode}></BeamInput>
          <BeamInput name={'description'} label={'Role Description'} required={true}></BeamInput>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <BeamCancelButton />
            <BeamSubmitButton />
          </Stack>
        </Form>
      </Formik>
    </>
  );
};

export default RolesForm;
